import { useEffect, useState, useRef, useCallback } from 'react';
import { TrackingSession, TrackingSessionState, TrackingSessionConfig } from '@trackthatride/realtime';

export interface UseTrackingSessionResult {
  /** Current tracking state */
  state: TrackingSessionState | null;
  
  /** Whether the session is connected */
  isConnected: boolean;
  
  /** Connection status */
  connectionStatus: 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
  
  /** Connect to a specific tracking code */
  connect: (trackingCode: string) => Promise<void>;
  
  /** Disconnect from tracking */
  disconnect: () => void;
  
  /** Manually refresh tracking data */
  refresh: () => Promise<void>;
  
  /** Any error that occurred */
  error: Error | null;
}

/**
 * React hook for managing tracking sessions
 */
export function useTrackingSession(
  config: TrackingSessionConfig = {}
): UseTrackingSessionResult {
  const [state, setState] = useState<TrackingSessionState | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const trackingSession = useRef<TrackingSession | null>(null);
  const currentTrackingCode = useRef<string | null>(null);

  // Initialize session
  useEffect(() => {
    try {
      trackingSession.current = new TrackingSession({
        autoConnect: false, // We'll handle connection manually
        ...config
      });

      const unsubscribe = trackingSession.current.subscribe((newState) => {
        setState(newState);
        setError(null); // Clear errors on successful state updates
      });

      // Handle SSE errors
      trackingSession.current.onSSEEvent('error', (errorData) => {
        setError(new Error(errorData.message));
      });

      return () => {
        unsubscribe();
        if (trackingSession.current) {
          trackingSession.current.destroy();
          trackingSession.current = null;
        }
      };
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to initialize tracking session'));
    }
  }, [config]);

  const connect = useCallback(async (trackingCode: string) => {
    if (!trackingSession.current) {
      throw new Error('Tracking session not initialized');
    }

    try {
      setError(null);
      currentTrackingCode.current = trackingCode;
      await trackingSession.current.connect(trackingCode);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to connect to tracking');
      setError(error);
      throw error;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (trackingSession.current) {
      trackingSession.current.disconnect();
      currentTrackingCode.current = null;
      setState(null);
      setError(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!trackingSession.current || !currentTrackingCode.current) {
      throw new Error('No active tracking session to refresh');
    }

    try {
      setError(null);
      // Reconnect to get fresh data
      await trackingSession.current.connect(currentTrackingCode.current);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to refresh tracking data');
      setError(error);
      throw error;
    }
  }, []);

  return {
    state,
    isConnected: trackingSession.current?.isConnected() ?? false,
    connectionStatus: state?.connectionStatus ?? 'disconnected',
    connect,
    disconnect,
    refresh,
    error
  };
}

export default useTrackingSession;